import ActivityChipTableCell from 'Components/ActivityChipTableCell'
import ClickableTableCell from 'Components/ClickableTableCell'
import CustomButton from 'Components/CustomButton'
import CustomTablePagination from 'Components/CustomTablePagination/CustomTablePagination'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import EnhancedTableHead from 'Components/EnhancedTableHead/EnhancedTableHead'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useGetDevicesQuery } from 'Services/ServiceProviderManagement/Features/Device/deviceApi'
import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import type { FetchDeviceListParams } from 'Services/ServiceProviderManagement/Features/Device/types'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import type { SalesPoint } from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import {
  PTA_PARAM_KEY,
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC
} from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import useListFunctions from 'Util/hooks/useListFunctions'
import { appendQueryParamsToUrl } from 'Util/url'
import AddIcon from '@mui/icons-material/Add'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface SalesPointDevicesProps {
  salesPoint: SalesPoint
}

export default function SalesPointDevices({ salesPoint }: Readonly<SalesPointDevicesProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/devices`
  const navigate = useNavigate()

  const headCells: HeadCell[] = [
    {
      id: 'deviceId',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Device name`,
      sortable: true
    },
    {
      id: 'activity',
      numeric: false,
      disablePadding: false,
      label: t`Activity`,
      sortable: false
    }
  ]

  /**
   * Query params are separated from searchParams
   * to avoid affecting sales point list page state.
   */
  const [searchQueryParams, setSearchQueryParams] = useState<Partial<FetchDeviceListParams>>({
    start_index: undefined,
    count: ROWS_PER_PAGE_DEFAULT,
    sort_by: 'deviceId',
    sort_order: SORT_ORDER_ASC
  })

  const { handleChangePage, handleRequestSort } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const {
    data: { resources: devices } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching: isDevicesFetching
  } = useGetDevicesQuery(
    {
      ...searchQueryParams,
      sales_point_id: salesPoint.sales_point_id
    },
    { skip: !salesPoint }
  )

  const orderPrefix = `${searchQueryParams.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${searchQueryParams.sort_by}`

  const navigateToDevicePage = (deviceId: string) => {
    return navigate(
      appendQueryParamsToUrl(baseUrl, {
        [SELECTED_DEVICE_PARAMETER_KEY]: deviceId,
        [PTA_PARAM_KEY]: salesPoint.pta
      })
    )
  }

  return (
    <Box>
      <WithLoader loading={isDevicesFetching}>
        <Paper sx={{ width: '100%', mb: 2, pt: 2 }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size="small">
              <EnhancedTableHead
                orderBy={orderBy}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {devices.length > 0 ? (
                  devices.map(row => {
                    return (
                      <StyledTableRow hover tabIndex={-1} key={row.device_id}>
                        <ClickableTableCell onClick={() => navigateToDevicePage(row.device_id)}>
                          {showValueOrHyphen(row.device_id)}
                        </ClickableTableCell>
                        <ClickableTableCell onClick={() => navigateToDevicePage(row.device_id)}>
                          {showValueOrHyphen(row.name)}
                        </ClickableTableCell>
                        <ActivityChipTableCell active={row.active} />
                      </StyledTableRow>
                    )
                  })
                ) : (
                  <StyledTableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2">{t`Sales point has no devices.`}</Typography>
                    </TableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalResults}
            page={
              searchQueryParams.start_index
                ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                : 0
            }
            onPageChange={handleChangePage}
          />
        </Paper>
        <CustomButton
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          sx={{ ml: 2, mb: 2 }}
          onClick={() =>
            navigate(
              `${baseUrl}/create?${SELECTED_SALES_POINT_PARAMETER_KEY}=${salesPoint.sales_point_id}`
            )
          }
        >{t`Add device`}</CustomButton>
      </WithLoader>
    </Box>
  )
}
